// Tables/cells · AssignCell
//
// Generic inline-assign cell. Pill-over-EntitySelect: shows current assignee
// as a clickable `<Pill>` (Avatar + name), opens an `<EntitySelect>` inline
// on click to pick a different one. Optional hover-revealed "assign-to-me"
// button when `quickAssign` is set.
//
// Entity-agnostic by design. The legacy transactions-specific
// `src/entities/transactions/cells/AssignCell.tsx` is the reference, but this
// generic version carries zero transaction knowledge — caller binds the
// assignee + quick-assign + select kind via the schema's `data` accessor and
// commits via `onCommit`.
//
// Example schema row:
//
//   logistics_employee: {
//     component: AssignCell,
//     data: (r) => ({
//       assignee: r.logistics_employee
//         ? { id: r.logistics_employee.id, name: r.logistics_employee.full_name }
//         : null,
//       quickAssign: r.__myEmployeeId
//         ? { id: r.__myEmployeeId, label: "Przypisz do mnie" }
//         : undefined,
//       entitySelectKind: "employee",
//       entitySelectRole: "logistics",
//     }),
//     onCommit: (r, next, extras) =>
//       (extras as TxExtras).actions.assign(r.id, next as number | null),
//   }

import React, { useRef, useState } from "react";
import type { ReactNode } from "react";
import { Space, Tooltip } from "antd";
import { CloseOutlined, LoadingOutlined, UserAddOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Button } from "@ds/primitives/Button";
import { useEntitySelectRenderer } from "@ds/display/entitySelectRendererContext";
import { Avatar } from "@ds/display/Avatar";
import { Pill } from "@ds/display/Pill";
import { TextValue } from "@ds/display/TextValue";
import { Inline } from "@ds/layout/Inline";
import { TONE } from "@ds/tokens/palette";

// ─── Inputs (data props — the model) ─────────────────────────────────────

export interface AssignCellAssignee {
  id: string | number;
  name: string;
  /** Optional image URL — when set, the Avatar shows a photo instead of initials. */
  avatar?: string | null;
}

export interface AssignCellQuickAssign {
  /** Id of the quick-assign target (typically the current user's employee/user id). */
  id: string | number;
  /** Tooltip label for the hover-revealed button (e.g. "Przypisz do mnie"). */
  label: string;
}

export interface AssignCellInput {
  /** Currently-assigned entity (or null when unassigned). */
  assignee: AssignCellAssignee | null;
  /** When set AND quickAssign.id !== assignee?.id, a hover icon button is
   *  rendered next to the pill. Click → onCommit(quickAssign.id). */
  quickAssign?: AssignCellQuickAssign;
  /** Display text for the empty (unassigned) pill. Default "Nieprzypisana". */
  placeholder?: string;
  /** `EntitySelect.kind` for the edit-mode dropdown. Default `"employee"`. */
  entitySelectKind?: string;
  /** Optional `EntitySelect.role` filter (employees-only kinds). */
  entitySelectRole?: string;
  /** Stable name forwarded into testids (`assign-cell-{name}`). */
  name?: string;
  /** `data-testid` on the root container. */
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ─────────────────────────

export interface AssignCellOutput {
  /** Commit handler. Called with the next assignee id (or null for clear).
   *  When unset the cell renders as read-only (no edit affordance, no
   *  quick-assign button). */
  onCommit?: (next: string | number | null) => void | Promise<void>;
}

export type AssignCellProps = AssignCellInput & AssignCellOutput;

// ─── Implementation ──────────────────────────────────────────────────────
const ENTITY_SELECT_WIDTH = 140;
const BLUR_CLOSE_MS = 200;

export function AssignCell({
  assignee,
  quickAssign,
  placeholder,
  entitySelectKind = "employee",
  entitySelectRole,
  name,
  testId,
  onCommit,
}: AssignCellProps): ReactNode {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t("table.assign.unassigned", "Nieprzypisana");
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const blurTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const renderSelect = useEntitySelectRenderer();
  const editable = !!onCommit;

  const handleCommit = async (next: string | number | null) => {
    if (!onCommit) return;
    clearTimeout(blurTimeout.current);
    const result = onCommit(next);
    if (result && typeof (result).then === "function") {
      setLoading(true);
      try {
        await result;
      } finally {
        setLoading(false);
        setEditing(false);
      }
    } else {
      setEditing(false);
    }
  };

  const handleBlur = () => {
    blurTimeout.current = setTimeout(() => setEditing(false), BLUR_CLOSE_MS);
  };

  // Loading takes precedence over everything else.
  if (loading) {
    return (
      <span data-testid={testId}>
        <LoadingOutlined style={{ color: TONE.blue.base }} />
      </span>
    );
  }

  // Edit-state — only when editable AND user opened it.
  if (editing && editable) {
    return (
      <Space
        size={2}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        {renderSelect({
          kind: entitySelectKind,
          role: entitySelectRole,
          size: "small",
          style: { width: ENTITY_SELECT_WIDTH },
          value: assignee?.id,
          autoFocus: true,
          onChange: (val: string | number | null) => handleCommit(val ?? null),
          onBlur: handleBlur,
          testId: name ? `assign-cell-${name}-select` : "assign-cell-select",
        })}
        {quickAssign && quickAssign.id !== assignee?.id && (
          <Tooltip title={quickAssign.label}>
            <Button
              label=""
              size="sm"
              variant="default"
              icon={<UserAddOutlined />}
              testId={name ? `assign-cell-${name}-quick` : "assign-cell-quick"}
              onClick={() => {
                clearTimeout(blurTimeout.current);
                void handleCommit(quickAssign.id);
              }}
            />
          </Tooltip>
        )}
      </Space>
    );
  }

  // Display state — pill (clickable when editable).
  const display = assignee ? (
    <Pill
      tone="grey"
      onClick={editable ? () => setEditing(true) : undefined}
      testId={name ? `assign-cell-${name}-pill` : "assign-cell-pill"}
    >
      <Inline gap="xs" align="center">
        <Avatar size="sm" name={assignee.name} src={assignee.avatar ?? undefined} />
        <TextValue size="xs">{assignee.name}</TextValue>
      </Inline>
    </Pill>
  ) : (
    <Pill
      tone="grey"
      onClick={editable ? () => setEditing(true) : undefined}
      testId={name ? `assign-cell-${name}-pill` : "assign-cell-pill"}
    >
      <TextValue italic size="caption">{resolvedPlaceholder}</TextValue>
    </Pill>
  );

  const showQuickAssign =
    editable && quickAssign && quickAssign.id !== assignee?.id;
  // Inline unassign affordance — lets operators clear the assignee straight
  // from the resting cell without opening the picker. Shown only when there
  // is something to clear and the cell is editable.
  const showClear = editable && !!assignee;

  return (
    <Space
      size={2}
      onClick={(e) => e.stopPropagation()}
      data-testid={testId}
    >
      {display}
      {showQuickAssign && (
        <Tooltip title={quickAssign.label}>
          <Button
            label=""
            size="sm"
            variant="text"
            icon={<UserAddOutlined />}
            testId={name ? `assign-cell-${name}-quick` : "assign-cell-quick"}
            onClick={() => void handleCommit(quickAssign.id)}
          />
        </Tooltip>
      )}
      {showClear && (
        <Tooltip title={t("table.assign.unassign", "Usuń przypisanie")}>
          <Button
            label=""
            size="sm"
            variant="text"
            icon={<CloseOutlined />}
            ariaLabel={t("table.assign.unassign", "Usuń przypisanie")}
            testId={name ? `assign-cell-${name}-clear` : "assign-cell-clear"}
            onClick={() => void handleCommit(null)}
          />
        </Tooltip>
      )}
    </Space>
  );
}
